import {
  useCallback,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { doc, getFirestore, setDoc } from "firebase/firestore";
import { useRutasActions, useRutasState } from "../../../context/AppContext";
import { formatearNumeros } from "../../../utils/formatters";
import type {
  TablaAdministradoresModalType,
  TablaAdministradoresRuta,
  UseTablaAdministradoresReturn,
} from "./types";

export const useTablaAdministradores = (): UseTablaAdministradoresReturn => {
  const [activeModal, setActiveModal] =
    useState<TablaAdministradoresModalType | null>(null);
  const [selectedRuta, setSelectedRuta] =
    useState<TablaAdministradoresRuta | null>(null);
  const [submittingRutaUid, setSubmittingRutaUid] = useState<string | null>(
    null
  );
  const { rutasData: contextRutasData } = useRutasState();
  const { setRutasData } = useRutasActions();

  const rutasData = useMemo(
    () => (contextRutasData ?? []) as TablaAdministradoresRuta[],
    [contextRutasData]
  );

  const setModalState = useCallback(
    (
      modal: TablaAdministradoresModalType,
      value: SetStateAction<boolean>
    ): void => {
      setActiveModal((currentModal) => {
        const isCurrentModalActive = currentModal === modal;
        const nextValue =
          typeof value === "function" ? value(isCurrentModalActive) : value;

        if (nextValue) {
          return modal;
        }

        return isCurrentModalActive ? null : currentModal;
      });
    },
    []
  );

  const setShowModalDelete: Dispatch<SetStateAction<boolean>> = useCallback(
    (value) => {
      setModalState("delete", value);
    },
    [setModalState]
  );

  const setShowModalEdit: Dispatch<SetStateAction<boolean>> = useCallback(
    (value) => {
      setModalState("edit", value);
    },
    [setModalState]
  );

  const setVerDetalles: Dispatch<SetStateAction<boolean>> = useCallback(
    (value) => {
      setModalState("detalles", value);
    },
    [setModalState]
  );

  const openModal = useCallback(
    (modal: TablaAdministradoresModalType, ruta: TablaAdministradoresRuta) => {
      setSelectedRuta(ruta);
      setActiveModal(modal);
    },
    []
  );

  const handleEditClick = useCallback(
    (ruta: TablaAdministradoresRuta): void => {
      openModal("edit", ruta);
    },
    [openModal]
  );

  const handleDeleteClick = useCallback(
    (ruta: TablaAdministradoresRuta): void => {
      openModal("delete", ruta);
    },
    [openModal]
  );

  const handleVerDetallesClick = useCallback(
    (ruta: TablaAdministradoresRuta): void => {
      openModal("detalles", ruta);
    },
    [openModal]
  );

  const handleRadioChange = useCallback(
    async (ruta: TablaAdministradoresRuta): Promise<void> => {
      if (!ruta.adminUid || !ruta.uid) {
        return;
      }

      const nextIsAutorized = !Boolean(ruta.isAutorized);

      setSubmittingRutaUid(ruta.uid);
      const db = getFirestore();

      try {
        const rutaRef = doc(db, "admin_users", ruta.adminUid, "rutas", ruta.uid);
        await setDoc(rutaRef, { isAutorized: nextIsAutorized }, { merge: true });

        setRutasData((previousRutas) =>
          previousRutas?.map((item) =>
            item.uid === ruta.uid
              ? { ...item, isAutorized: nextIsAutorized }
              : item
          ) ?? previousRutas
        );

        setSelectedRuta((previousSelectedRuta) =>
          previousSelectedRuta?.uid === ruta.uid
            ? { ...previousSelectedRuta, isAutorized: nextIsAutorized }
            : previousSelectedRuta
        );
      } catch (error) {
        console.log("error al seleccionar ruta", error);
      } finally {
        setSubmittingRutaUid(null);
      }
    },
    [setRutasData]
  );

  return {
    formatearNumeros,
    handleDeleteClick,
    handleEditClick,
    handleRadioChange,
    handleVerDetallesClick,
    rutasData,
    selectedRuta,
    submittingRutaUid,
    setSelectedRuta,
    setShowModalDelete,
    setShowModalEdit,
    setVerDetalles,
    showModalDelete: activeModal === "delete",
    showModalEdit: activeModal === "edit",
    verDetalles: activeModal === "detalles",
  };
};
